import * as React from "react"
import { Box, ListItem } from "@mui/material"
import { useRouter } from "next/router"
import Link from "next/link"
import { HiLockClosed } from "react-icons/hi"

export default function SidebarItem({
  href,
  icon: Icon,
  outlineIcon: OutlineIcon,
  rightAdornment: RightAdornment,
  title,
  children,
  activePaths,
  deemphasized,
  sx,
  ...rest
}: {
  href: string
  icon: React.ReactNode
  outlineIcon: React.ReactNode
  rightAdornment?: React.ReactNode
  title: string
  children?: React.ReactNode
  activePaths?: { path: string; strategy: "exact" | "startsWith" }[]
  deemphasized?: boolean
  sx: any
}) {
  const router = useRouter()
  const showChildren = children ? router.pathname.startsWith(href) : false
  // logic for determining when to highlight a sidebar item
  let activePathList = activePaths || [
    {
      path: href,
      strategy: "startsWith",
    },
  ]

  let itemIsActive = false
  activePathList.forEach(pathObj => {
    if (pathObj?.strategy === "exact") {
      if (pathObj?.path === router.pathname) {
        itemIsActive = true
      }
    } else if (pathObj?.strategy === "startsWith") {
      if (router.pathname.startsWith(pathObj?.path)) {
        itemIsActive = true
      }
    }
  })

  return (
    <>
      <ListItem
        disableGutters
        sx={{
          display: "flex",
          py: 0,
          px: 1.5,
          "&:hover": {
            bgcolor: itemIsActive ? "gray.300" : "gray.200",
          },
          ...((itemIsActive && {
            bgcolor: "gray.200",
          }) ||
            {}),
          ...sx,
        }}
        {...rest}
      >
        <Link href={href} passHref={true}>
          <Box
            component="a"
            color="info"
            sx={{
              textDecoration: "none",
              borderRadius: 2,
              alignItems: "center",
              justifyContent: "flex-start",
              letterSpacing: 0,
              py: 0.5,
              px: 1.25,
              textTransform: "none",
              display: "flex",
              width: "100%",
              color: itemIsActive ? "gray.700" : "gray.500",
              ...(deemphasized && {
                color: "gray.400",
              }),
              "&:hover": {
                bgcolor: "transparent",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "20px",
                height: "20px",
                mr: 1,
              }}
            >
              {Icon && itemIsActive && Icon}
              {OutlineIcon && !itemIsActive && OutlineIcon}
            </Box>
            <Box
              sx={{
                display: "flex",
                width: "100%",
                alignItems: "center",
                textOverflow: "ellipsis",
                fontSize: "sm",
                fontWeight: itemIsActive ? "600" : "medium",
              }}
            >
              {title}
              {RightAdornment && RightAdornment}
            </Box>
            {deemphasized && (
              <Box
                component="span"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "gray.300",
                  height: 12,
                }}
              >
                <HiLockClosed size={12} />
              </Box>
            )}
          </Box>
        </Link>
      </ListItem>
      {showChildren && children}
    </>
  )
}
